import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dayjs } from 'dayjs';
import { Box, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { managerStyles } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/styles.ts';
import ManagerApplicationsTable from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/ManagerApplicationsTable.tsx';
import type {
  TManagerApplicationFilters,
  TManagerApplicationRow,
  TManagerSegmentOption,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import {
  DEFAULT_MANAGER_APPLICATION_FILTERS,
  DOWNLOAD_REPORT_BUTTON_TOOLTIP,
  MANAGER_APPLICATION_CLEAR_SEARCH_BUTTON_ARIA_LABEL,
  MANAGER_APPLICATION_PAGE_SUBTITLE,
  MANAGER_APPLICATION_PAGE_TITLE,
  MANAGER_APPLICATION_REQUEST_DATE_FORMAT,
  MANAGER_APPLICATION_SEARCH_BUTTON_ARIA_LABEL,
  MANAGER_APPLICATION_SEARCH_PLACEHOLDER,
  MANAGER_STATUS_OPTIONS,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import { DateFilter } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/Filters/DateFilter.tsx';
import { SearchPanel } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/Filters/SearchPanel.tsx';
import { ReadyCovenantsDialog } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/ReadyCovenants/ReadyCovenantsDialog.tsx';
import { buildCovenantsManagerApplicationPath } from '~/modules/covenants/utils.ts';
import {
  buildManagerSegmentOptions,
  getManagerSortParam,
  getSupervisorApplicationsRequestKey,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/utils.ts';
import {
  selectSupervisorApplications,
  selectSupervisorApplicationsError,
  selectSupervisorApplicationsPageConfiguration,
  selectSupervisorSegments,
  selectSupervisorSegmentsStatus,
  selectSupervisorApplicationsStatus,
} from '~/store/covenants/selectors.ts';
import { fetchSupervisorApplications } from '~/store/covenants/thunks/supervisorApplicationThunks.ts';
import { fetchSupervisorSegments } from '~/store/covenants/thunks/supervisorSegmentThunks.ts';
import { FetchStatus } from '~/store/covenants/types.ts';
import { AppDispatch } from '~/store/store.ts';
import type { RootState } from '~/store/store.ts';
import {
  getManagerApplicationsSearchQueryForUrl,
  parseManagerApplicationsUrlState,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/managerApplicationsUrlUtils.ts';
import { useSyncManagerApplicationsUrlState } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/hooks/useSyncManagerApplicationsUrlState.ts';
import { useManagerApplicationAssignment } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/hooks/useManagerApplicationAssignment.ts';
import { useManagerApplicationsExport } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/hooks/useManagerApplicationsExport.ts';
import useGetUserRole from '~/hooks/useGetUserRole';

const CovenantsApplicationByManager: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const initialQueryState = useMemo(
    () => parseManagerApplicationsUrlState(location.search),
    [location.search]
  );
  const currentUser = useSelector((state: RootState) => state.users.user);
  const { isManagerLogin } = useGetUserRole();
  const supervisorApplications = useSelector(selectSupervisorApplications);
  const supervisorApplicationsPageConfiguration = useSelector(
    selectSupervisorApplicationsPageConfiguration
  );
  const supervisorSegments = useSelector(selectSupervisorSegments);
  const supervisorSegmentsStatus = useSelector(selectSupervisorSegmentsStatus);
  const supervisorApplicationsStatus = useSelector(selectSupervisorApplicationsStatus);
  const supervisorApplicationsError = useSelector(selectSupervisorApplicationsError);
  const currentManagerIdentityValues = useMemo(() => {
    if (!isManagerLogin) {
      return [];
    }

    return [currentUser.username, currentUser.email].filter(
      (value): value is string => typeof value === 'string' && !!value.trim()
    );
  }, [currentUser]);

  const currentDate = useMemo(() => new Date(), []);
  const [searchQuery, setSearchQuery] = useState(initialQueryState.searchQuery);
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState(initialQueryState.searchQuery);
  const [startDate, setStartDate] = useState<Dayjs | null>(initialQueryState.startDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(initialQueryState.endDate);
  const [tableFilters, setTableFilters] = useState<TManagerApplicationFilters>(() => ({
    ...DEFAULT_MANAGER_APPLICATION_FILTERS,
    page: initialQueryState.page,
    rowsPerPage: initialQueryState.rowsPerPage,
    sortKey: initialQueryState.sortKey,
    sortDirection: initialQueryState.sortDirection,
    status: initialQueryState.status,
    assignee: initialQueryState.assignee,
    segmentIds: initialQueryState.segmentIds,
  }));
  const [modalErrorText, setModalErrorText] = useState('');
  const [selectedCovenantsApplicationId, setSelectedCovenantsApplicationId] = useState<
    string | null
  >(null);
  const [hasLoadedApplicationsOnce, setHasLoadedApplicationsOnce] = useState(false);
  const lastAutoFetchRequestKeyRef = useRef<string | null>(null);

  const setManagerModalError = useCallback((value: string | null) => {
    setModalErrorText(value ?? '');
  }, []);

  const supervisorApplicationsRequest = useMemo(
    () => ({
      page: tableFilters.page,
      size: tableFilters.rowsPerPage,
      fromDate: startDate?.format(MANAGER_APPLICATION_REQUEST_DATE_FORMAT) ?? '',
      toDate: endDate?.format(MANAGER_APPLICATION_REQUEST_DATE_FORMAT) ?? '',
      search: submittedSearchQuery || undefined,
      assignee: !tableFilters.assignee.length ? undefined : tableFilters.assignee,
      segmentId: !tableFilters.segmentIds.length ? undefined : tableFilters.segmentIds,
      status: !tableFilters.status.length ? [...MANAGER_STATUS_OPTIONS] : tableFilters.status,
      sort: getManagerSortParam(tableFilters.sortKey, tableFilters.sortDirection),
    }),
    [
      endDate,
      startDate,
      submittedSearchQuery,
      tableFilters.assignee,
      tableFilters.segmentIds,
      tableFilters.page,
      tableFilters.rowsPerPage,
      tableFilters.sortDirection,
      tableFilters.sortKey,
      tableFilters.status,
    ]
  );

  const supervisorApplicationsRequestKey = useMemo(
    () => getSupervisorApplicationsRequestKey(supervisorApplicationsRequest),
    [supervisorApplicationsRequest]
  );

  const managerApplicationsListSearch = useSyncManagerApplicationsUrlState({
    page: tableFilters.page,
    rowsPerPage: tableFilters.rowsPerPage,
    submittedSearchQuery,
    startDate,
    endDate,
    sortKey: tableFilters.sortKey,
    sortDirection: tableFilters.sortDirection,
    status: tableFilters.status,
    assignee: tableFilters.assignee,
    segmentIds: tableFilters.segmentIds,
  });

  const segmentOptions = useMemo<TManagerSegmentOption[]>(
    () => buildManagerSegmentOptions(supervisorSegments),
    [supervisorSegments]
  );

  const {
    assignmentAction,
    assignedByCurrentManagerIds,
    handleAssignApplication,
    handleUnassignApplication,
  } = useManagerApplicationAssignment({
    supervisorApplicationsRequest,
    managerApplicationsListSearch,
    onError: setManagerModalError,
  });

  const resetPageToStart = useCallback(() => {
    setTableFilters((previousState) => ({
      ...previousState,
      page: 0,
    }));
  }, []);

  const handleClearDates = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    resetPageToStart();
  }, [resetPageToStart]);

  const { isExportLoading, isDownloadDisabled, handleDownloadReport } =
    useManagerApplicationsExport({
      request: supervisorApplicationsRequest,
      onError: setManagerModalError,
    });

  const handleStartDateChange = useCallback(
    (nextStartDate: Dayjs | null) => {
      setStartDate(nextStartDate);
      resetPageToStart();
    },
    [resetPageToStart]
  );

  const handleEndDateChange = useCallback(
    (nextEndDate: Dayjs | null) => {
      setEndDate(nextEndDate);
      resetPageToStart();
    },
    [resetPageToStart]
  );

  const handleSearchChange = useCallback(
    (nextSearchQuery: string) => {
      setSearchQuery(nextSearchQuery);

      if (!nextSearchQuery.length) {
        setSubmittedSearchQuery('');
        resetPageToStart();
      }
    },
    [resetPageToStart]
  );

  const handleSearchSubmit = useCallback(() => {
    setSubmittedSearchQuery(getManagerApplicationsSearchQueryForUrl(searchQuery));
    resetPageToStart();
  }, [resetPageToStart, searchQuery]);

  const handleViewApplication = useCallback(
    (application: TManagerApplicationRow) => {
      navigate(buildCovenantsManagerApplicationPath(application.id, managerApplicationsListSearch));
    },
    [managerApplicationsListSearch, navigate]
  );

  const handleTableFiltersChange = useCallback(
    (updater: (previousState: TManagerApplicationFilters) => TManagerApplicationFilters) => {
      setTableFilters((previousState) => updater(previousState));
    },
    []
  );

  const handleOpenCovenants = useCallback((application: TManagerApplicationRow) => {
    setSelectedCovenantsApplicationId(application.id);
  }, []);

  const handleCloseCovenantsDialog = useCallback(() => {
    setSelectedCovenantsApplicationId(null);
  }, []);

  useEffect(() => {
    if (!isManagerLogin) {
      return;
    }

    if (supervisorSegmentsStatus === FetchStatus.Idle) {
      void dispatch(fetchSupervisorSegments());
    }
  }, [dispatch, isManagerLogin, supervisorSegmentsStatus]);

  useEffect(() => {
    if (!isManagerLogin) {
      return;
    }

    if (lastAutoFetchRequestKeyRef.current === supervisorApplicationsRequestKey) {
      return;
    }

    lastAutoFetchRequestKeyRef.current = supervisorApplicationsRequestKey;
    dispatch(fetchSupervisorApplications({ ...supervisorApplicationsRequest }));
  }, [isManagerLogin, dispatch, supervisorApplicationsRequest, supervisorApplicationsRequestKey]);

  useEffect(() => {
    if (supervisorApplicationsError?.message) {
      setModalErrorText(supervisorApplicationsError.message);
    }
  }, [supervisorApplicationsError]);

  useEffect(() => {
    if (
      supervisorApplicationsStatus === FetchStatus.Success ||
      supervisorApplicationsStatus === FetchStatus.Error
    ) {
      setHasLoadedApplicationsOnce(true);
    }
  }, [supervisorApplicationsStatus]);

  if (!isManagerLogin) {
    return null;
  }

  const isInitialLoading =
    !hasLoadedApplicationsOnce && supervisorApplicationsStatus === FetchStatus.Loading;

  if (isInitialLoading) {
    return (
      <Box sx={commonStyles.centeredLoader}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={commonStyles.pageRoot}>
      <Box sx={managerStyles.pageContent}>
        <Box sx={commonStyles.header}>
          <Box>
            <Typography variant="h4" sx={commonStyles.title}>
              {MANAGER_APPLICATION_PAGE_TITLE}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" sx={commonStyles.subtitle}>
              {MANAGER_APPLICATION_PAGE_SUBTITLE}
            </Typography>
          </Box>
        </Box>
        <Divider sx={managerStyles.pageDivider} />

        <Box sx={commonStyles.content}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <DateFilter
                currentDate={currentDate}
                startDate={startDate}
                setStartDate={handleStartDateChange}
                endDate={endDate}
                setEndDate={handleEndDateChange}
                onClearDates={handleClearDates}
                onDownloadReport={handleDownloadReport}
                isDownloadDisabled={isDownloadDisabled}
                isDownloadLoading={isExportLoading}
                downloadTooltip={DOWNLOAD_REPORT_BUTTON_TOOLTIP}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <SearchPanel
                searchQuery={searchQuery}
                placeholder={MANAGER_APPLICATION_SEARCH_PLACEHOLDER}
                searchButtonAriaLabel={MANAGER_APPLICATION_SEARCH_BUTTON_ARIA_LABEL}
                clearButtonAriaLabel={MANAGER_APPLICATION_CLEAR_SEARCH_BUTTON_ARIA_LABEL}
                onSearchChange={handleSearchChange}
                onSearchSubmit={handleSearchSubmit}
              />
            </Grid>
          </Grid>

          <ManagerApplicationsTable
            applications={supervisorApplications}
            assignedByCurrentManagerIds={assignedByCurrentManagerIds}
            currentManagerIdentityValues={currentManagerIdentityValues}
            filters={tableFilters}
            totalElements={supervisorApplicationsPageConfiguration.totalElements}
            isLoading={supervisorApplicationsStatus === FetchStatus.Loading}
            activeAssignmentAction={assignmentAction}
            segmentOptions={segmentOptions}
            onAssignApplication={handleAssignApplication}
            onUnassignApplication={handleUnassignApplication}
            onViewApplication={handleViewApplication}
            onOpenCovenants={handleOpenCovenants}
            onFiltersChange={handleTableFiltersChange}
          />
        </Box>
      </Box>
      <ModalAlert
        isOpen={!!modalErrorText}
        text={modalErrorText}
        onClose={() => setModalErrorText('')}
      />
      <ReadyCovenantsDialog
        open={!!selectedCovenantsApplicationId}
        onClose={handleCloseCovenantsDialog}
        applicationId={selectedCovenantsApplicationId}
      />
    </Box>
  );
};

export default CovenantsApplicationByManager;
